import { useActiveBundle } from '@/context/active-bundle';
import { useCurrentWorkspace } from '@/context/current-workspace';
import type { TargetTransition, TargetTransitionOptions } from '@/services/router/safe-navigate';
import type { MergedWorkspace } from '@/features/workspaces';
import { useFilterWorkspacesByName, useWorkspaceCount } from '@/features/workspaces/hooks';
import { getWorkspaceSettingsLink } from '@/hooks/get-workspace-settings-link';
import { FeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { AppBundle } from '@/utils/app-bundles';
import { workspaceRootUrl } from '@/utils/navigation';
import { useSampleWorkspace } from '@/features/workspaces/sample-workspace';
import {
  Box,
  Button,
  Icon,
  List,
  ListItem,
  PlainButton,
  PlainLink,
  Popover,
  PopoverContent,
  Search,
  Text,
  TextLabel,
  Tooltip,
  Truncated,
} from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';

import type { Ref } from 'react';
import { useEffect, useRef, useState } from 'react';
import { WorkspaceDot } from '../../../workspace-dot';
import * as styles from './index.css';

function isWorkspaceActive(
  workspace: MergedWorkspace,
  currentWorkspace: MergedWorkspace | null,
  activeApp: AppBundle | null,
) {
  if (activeApp === AppBundle.Journeys) {
    return workspace.cio_workspace_id === currentWorkspace?.cio_workspace_id;
  } else if (activeApp === AppBundle.Pipelines) {
    return workspace.cdp_workspace_id === currentWorkspace?.cdp_workspace_id;
  }

  return false;
}

function SampleWorkspaceItem() {
  return (
    <ListItem>
      <Tooltip
        content="This workspace is a sample workspace with pretend data. You can view the data, but you can't make changes."
        placement="right"
        isCustom={true}
      >
        {(context) => (
          <PlainLink
            href="/sample-workspace"
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps()}
          >
            <Box p="100" backgroundColor="success-minimal" className={styles.sampleWorkspaceContainer}>
              <Box p="100">
                <Box>
                  <Text>Sample Workspace</Text>
                </Box>
                <Box className={styles.sampleWorkspaceLabelContainer}>
                  <Text textTransform="uppercase" weight="semibold" className={styles.sampleWorkspaceLabel}>
                    Pretend data, view only
                  </Text>
                </Box>
              </Box>
            </Box>
          </PlainLink>
        )}
      </Tooltip>
    </ListItem>
  );
}

function WorkspaceDropdownWorkspace(props: {
  workspace: MergedWorkspace;
  onWorkspaceLinkClick: (event: React.MouseEvent<HTMLAnchorElement>, opts?: TargetTransitionOptions) => void;
  currentWorkspaceRef: Ref<HTMLLIElement> | null;
  highlight?: boolean;
}) {
  const { workspace, onWorkspaceLinkClick } = props;
  const currentWorkspace = useCurrentWorkspace();
  const activeApp = useActiveBundle();
  const isActive = isWorkspaceActive(workspace, currentWorkspace, activeApp);
  const workspaceSettingsLink = getWorkspaceSettingsLink();

  const topLevelTransition: TargetTransition = {
    url: workspaceRootUrl(workspace),
    options: {
      shouldFadeOut: workspace.id !== currentWorkspace?.id,
      shouldUnrenderOutlet: false,
    },
  };

  return (
    <ListItem className={styles.workspaceItem} aria-current={isActive} ref={props.currentWorkspaceRef}>
      <a
        href={topLevelTransition.url}
        className={styles.workspaceItemLink}
        onClick={(event) => {
          onWorkspaceLinkClick(event, topLevelTransition.options);
        }}
      >
        <div className={styles.workspaceItemNameContainer}>
          {!isEmptyString(workspace.color) ? <WorkspaceDot color={workspace.color} /> : null}
          <Truncated className={styles.workspaceItemName} aria-label={`${workspace.name} workspace`}>
            {workspace.name}
          </Truncated>
        </div>
      </a>
      <PlainLink
        className={styles.workspaceItemSettingsLink}
        aria-label={`${workspace.name} workspace settings`}
        href={workspaceSettingsLink(workspace.id)}
        onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
          onWorkspaceLinkClick(event);
        }}
      >
        <Icon name="manage" />
      </PlainLink>
    </ListItem>
  );
}

function WorkspaceDropdown(props: {
  onWorkspaceLinkClick: (event: React.MouseEvent<HTMLAnchorElement>, opts?: TargetTransitionOptions) => void;
}) {
  const currentWorkspace = useCurrentWorkspace();

  const [workspaceSearchQuery, setWorkspaceSearchQuery] = useState<string>('');
  const workspaceCountQuery = useWorkspaceCount();
  const { isSampleWorkspace } = useSampleWorkspace();
  const isInteractiveDemoWorkspaceFeaureFlagEnabled = useFeatureFlag(FeatureFlag.InteractiveDemoWorkspace);
  const isSampleWorkspaceFeatureFlagEnabled = useFeatureFlag(FeatureFlag.SampleWorkspace);
  const isSampleWorkspaceEnabled = isInteractiveDemoWorkspaceFeaureFlagEnabled || isSampleWorkspaceFeatureFlagEnabled;
  const filteredWorkspacesQuery = useFilterWorkspacesByName(workspaceSearchQuery, {
    includeSampleWorkspace: isSampleWorkspaceEnabled,
  });
  const allWorkspacesCount = workspaceCountQuery.data ?? 0;
  const workspaceData = filteredWorkspacesQuery.data ?? [];

  const currentWorkspaceElementRef = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    if (currentWorkspaceElementRef.current == null) {
      return;
    }

    currentWorkspaceElementRef.current.scrollIntoView({
      block: 'center',
    });
  }, []);

  return (
    <PopoverContent className={styles.content}>
      <Box px="200" py="200">
        {allWorkspacesCount > 3 ? (
          <Search
            value={workspaceSearchQuery}
            onSearchInput={setWorkspaceSearchQuery}
            onSearch={setWorkspaceSearchQuery}
            shouldSearchOnClear={true}
            shouldSearchOnInput={true}
            placeholder="Find a workspace&hellip;"
            autoFocus={true}
            label="Workspaces"
            icon={null}
            w="full"
            maxW="full"
          />
        ) : (
          <TextLabel>Workspaces</TextLabel>
        )}
      </Box>
      <section className={styles.workspaceList}>
        <List styleType="none">
          {workspaceData.length > 0 ? (
            workspaceData.map((w: MergedWorkspace) => {
              if (isSampleWorkspaceEnabled && isSampleWorkspace(w.cio_workspace_id)) {
                return <SampleWorkspaceItem key={w.id} />;
              }
              return (
                <WorkspaceDropdownWorkspace
                  key={w.id}
                  workspace={w}
                  onWorkspaceLinkClick={props.onWorkspaceLinkClick}
                  currentWorkspaceRef={w.id === currentWorkspace?.id ? currentWorkspaceElementRef : null}
                />
              );
            })
          ) : (
            <ListItem>
              <TextLabel m="200" variant="secondary">
                No results found
              </TextLabel>
            </ListItem>
          )}
        </List>
      </section>
      <Box as="section" display="flex" alignItems="center" py="150" px="200">
        <Button
          href="/settings/workspaces"
          flexGrow="1"
          onClick={(event) => {
            props.onWorkspaceLinkClick(event as React.MouseEvent<HTMLAnchorElement>);
          }}
        >
          Manage all workspaces
        </Button>
      </Box>
    </PopoverContent>
  );
}

export function WorkspaceSelector(props: {
  onLinkClick: (event: React.MouseEvent<HTMLAnchorElement>, opts?: TargetTransitionOptions) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const currentWorkspace = useCurrentWorkspace();

  const onWorkspaceLinkClick = async (event: React.MouseEvent<HTMLAnchorElement>, opts?: TargetTransitionOptions) => {
    props.onLinkClick(event, opts);
    setIsOpen(false);
  };

  return (
    <>
      <Popover isOpen={isOpen} onOpenChange={setIsOpen} placement="bottom-start" withArrow={false}>
        {(context) => (
          <>
            <PlainButton
              className={styles.workspaceSelectorButton}
              aria-label={`${currentWorkspace?.name} workspace`}
              aria-description={`Click to open workspace selector.`}
              ref={context.reference.setReference}
              {...context.reference.getReferenceProps()}
            >
              <div className={styles.workspaceSelectorButtonWorkspaceName}>{currentWorkspace?.name}</div>

              <div className={styles.workspaceSelectorButtonChevron}>
                <Icon name="chevron-toggle" size="sm" />
              </div>
            </PlainButton>
            <WorkspaceDropdown onWorkspaceLinkClick={onWorkspaceLinkClick} />
          </>
        )}
      </Popover>
    </>
  );
}
